// Plan catalog -- the ONE place plan copy lives (plan audit 2026-09-28,
// tiered limits 2026-09-29: "customers must not abuse our LLM calls").
// Every claim here maps to a real gate; the backend source of truth is
// services/common/plans.py:
//   - AI numbers come live from GET /api/ai/plans (`quotas`); every plan,
//     Premium included, has a monthly cap -- nothing is "unlimited";
//   - saved resumes, templates, share links, Digital Portfolio + gallery are
//     enforced by resume-service; clean downloads by ai-service's export gate
//     (`limits`, mirrored in PLAN_LIMITS below as a pre-load fallback);
//   - Job Search / Tracker / LinkedIn profile generation = Premium.
// Prices come from GET /api/payments/tiers; the numbers below are only
// fallbacks for before that loads.

export const PLAN_ORDER = ["free", "starter", "professional", "premium"];

export const PLAN_META = {
  free: { name: "Free", price: 0, tagline: "Try every tool", paid: false },
  starter: { name: "Starter", price: 4.99, tagline: "Ready to apply", paid: true },
  professional: { name: "Professional", price: 9.99, tagline: "Actively applying", paid: true, recommended: true },
  premium: { name: "Premium", price: 19.99, tagline: "Your full job search", paid: true },
};

// Mirrors common/plans.py LIMITS (the live copy from /api/ai/plans replaces
// it once loaded -- see loadPlanCatalog).
export let PLAN_LIMITS = {
  free: { saved_resumes: 1, clean_downloads: 0, share_links: 0, templates: "basic", smart_layouts: false, portfolio: false, portfolio_gallery: false, job_search: false },
  starter: { saved_resumes: 3, clean_downloads: 10, share_links: 1, templates: "all", smart_layouts: false, portfolio: false, portfolio_gallery: false, job_search: false },
  professional: { saved_resumes: 10, clean_downloads: 50, share_links: 5, templates: "all", smart_layouts: true, portfolio: true, portfolio_gallery: false, job_search: false },
  premium: { saved_resumes: 30, clean_downloads: 200, share_links: 20, templates: "all", smart_layouts: true, portfolio: true, portfolio_gallery: true, job_search: true },
};
// Mirrors common/plans.py AI_QUOTAS so plan cards still list AI limits if
// /api/ai/plans is slow or unreachable; the live copy always wins.
export const PLAN_QUOTAS = {
  free: { resume_parse: 1, resume_parse_file: 1, ats_score: 1, job_match: 1, resume_compile: 0, cover_letter: 1, cover_letter_teaser: 2, ai_suggest: 3, grammar_fix: 3, ai_chat: 5, suggest_template: 0, linkedin_optimize: 0, linkedin_generate: 0, job_search_suggestions: 0, job_search_chat: 0, career_profile_check: 2 },
  starter: { resume_parse: 5, resume_parse_file: 5, ats_score: 10, job_match: 5, resume_compile: 3, cover_letter: 5, cover_letter_teaser: 10, ai_suggest: 30, grammar_fix: 30, ai_chat: 50, suggest_template: 0, linkedin_optimize: 3, linkedin_generate: 0, job_search_suggestions: 0, job_search_chat: 0, career_profile_check: 10 },
  professional: { resume_parse: 20, resume_parse_file: 20, ats_score: 30, job_match: 25, resume_compile: 15, cover_letter: 20, cover_letter_teaser: 40, ai_suggest: 150, grammar_fix: 150, ai_chat: 300, suggest_template: 20, linkedin_optimize: 15, linkedin_generate: 0, job_search_suggestions: 0, job_search_chat: 0, career_profile_check: 30 },
  premium: { resume_parse: 60, resume_parse_file: 60, ats_score: 100, job_match: 100, resume_compile: 50, cover_letter: 60, cover_letter_teaser: 120, ai_suggest: 500, grammar_fix: 500, ai_chat: 1000, suggest_template: 60, linkedin_optimize: 50, linkedin_generate: 20, job_search_suggestions: 100, job_search_chat: 300, career_profile_check: 100 },
};
// The 5 fixed templates on Free (Classic Executive, Academic Serif, Clean &
// Compact, Balanced Columns, Minimalist Timeline).
export let FREE_TEMPLATE_IDXS = [0, 3, 4, 6, 9];
export const DEFAULT_FREE_TEMPLATE_IDX = 4;
export const SMART_LAYOUT_IDX = -1;

export const planLimits = (plan) => PLAN_LIMITS[plan] || PLAN_LIMITS.free;

// Same rule as common/plans.template_allowed.
export function templateAllowed(plan, idx) {
  if (idx === null || idx === undefined) return true;
  const lim = planLimits(plan);
  if (idx === SMART_LAYOUT_IDX) return !!lim.smart_layouts;
  if (lim.templates === "all") return true;
  return FREE_TEMPLATE_IDXS.includes(idx);
}

// The cheapest plan that unlocks a template (for lock badges).
export const requiredPlanForTemplate = (idx) =>
  idx === SMART_LAYOUT_IDX ? "professional" : FREE_TEMPLATE_IDXS.includes(idx) ? "free" : "starter";

const lim = (plan, key, singular, plural) => {
  const v = planLimits(plan)[key];
  return `${v} ${v === 1 ? singular : plural}`;
};

const n = (q, key) => {
  const v = q?.[key];
  if (v === undefined || v === null) return null;
  return v === -1 ? "Unlimited" : String(v);
};

// Short card bullets. AI numbers are filled from live quotas.
export function planHighlights(plan, quotas) {
  const q = quotas?.[plan] || {};
  const per = (key, label, singular) => {
    const v = n(q, key);
    if (v === null || v === "0") return null;
    return `${v} ${v === "1" ? singular : label} / month`;
  };
  switch (plan) {
    case "free":
      return [
        `${lim("free", "saved_resumes", "saved resume", "saved resumes")} · 5 basic templates`,
        "PDF download (with watermark)",
        per("resume_parse", "AI resume generations", "AI resume generation"),
        per("ats_score", "ATS scans", "ATS scan"),
        per("job_match", "job matches", "job match"),
        per("cover_letter", "cover letters", "cover letter"),
      ].filter(Boolean);
    case "starter":
      return [
        `${lim("starter", "saved_resumes", "saved resume", "saved resumes")} · all 26 templates`,
        `${lim("starter", "clean_downloads", "clean PDF/DOCX download", "clean PDF/DOCX downloads")} / month`,
        `${lim("starter", "share_links", "share link", "share links")}`,
        per("ats_score", "ATS scans", "ATS scan"),
        per("job_match", "job matches", "job match"),
        per("resume_compile", "tailored resumes", "tailored resume"),
        per("cover_letter", "cover letters", "cover letter"),
      ].filter(Boolean);
    case "professional":
      return [
        `${lim("professional", "saved_resumes", "saved resume", "saved resumes")} · Smart Layouts + AI Style Studio`,
        `${lim("professional", "clean_downloads", "clean download", "clean downloads")} / month · ${lim("professional", "share_links", "share link", "share links")}`,
        "Digital Portfolio (publish)",
        per("ats_score", "ATS scans", "ATS scan"),
        per("job_match", "job matches", "job match"),
        per("resume_compile", "tailored resumes", "tailored resume"),
        per("cover_letter", "cover letters", "cover letter"),
      ].filter(Boolean);
    case "premium":
      return [
        `${lim("premium", "saved_resumes", "saved resume", "saved resumes")} · ${lim("premium", "clean_downloads", "clean download", "clean downloads")} / month`,
        "Portfolio with work gallery",
        "Job Search + saved searches & Tracker",
        "LinkedIn profile generation",
        per("job_match", "job matches", "job match"),
        per("resume_compile", "tailored resumes", "tailored resume"),
      ].filter(Boolean);
    default:
      return [];
  }
}

// Full comparison, grouped by product area. `quota` rows read live numbers;
// `access` rows are the non-quota gates listed at the top of this file.
export const COMPARE_GROUPS = [
  {
    area: "Resume Builder",
    rows: [
      { label: "Build & edit resumes, Career Profile", access: ["free", "starter", "professional", "premium"] },
      { label: "Saved resumes", limit: "saved_resumes" },
      { label: "AI resume generations", quota: "resume_parse" },
      { label: "AI polish (summary & bullets)", quota: "ai_suggest" },
      { label: "Grammar & spelling fixes", quota: "grammar_fix" },
      { label: "AI Copilot messages", quota: "ai_chat" },
    ],
  },
  {
    area: "Improve / Optimize",
    rows: [
      { label: "ATS scans", quota: "ats_score" },
      { label: "Tailored resumes (Tailor Resume)", quota: "resume_compile" },
    ],
  },
  {
    area: "Job Match",
    rows: [
      { label: "Job match analyses", quota: "job_match" },
      { label: "Cover letters", quota: "cover_letter" },
    ],
  },
  {
    area: "AI Style Studio & templates",
    rows: [
      { label: "Templates", text: { free: "5 basic", starter: "All 26", professional: "All 26", premium: "All 26" } },
      { label: "Smart Layouts & custom design", limit: "smart_layouts" },
      { label: "AI design recommendations", quota: "suggest_template" },
    ],
  },
  {
    area: "Downloads & sharing",
    rows: [
      { label: "PDF download", text: { free: "Watermarked", starter: "Clean", professional: "Clean", premium: "Clean" } },
      { label: "Clean PDF + DOCX downloads", limit: "clean_downloads", monthly: true },
      { label: "Active share links", limit: "share_links" },
      { label: "Digital Portfolio (publish)", limit: "portfolio" },
      { label: "Portfolio work gallery", limit: "portfolio_gallery" },
    ],
  },
  {
    area: "Job search & LinkedIn",
    rows: [
      { label: "LinkedIn rewrites", quota: "linkedin_optimize" },
      { label: "LinkedIn profile generation", access: ["premium"] },
      { label: "Job Search, saved searches & alerts", access: ["premium"] },
      { label: "Application previews & Tracker", access: ["premium"] },
    ],
  },
];

export function compareCell(row, plan, quotas) {
  if (row.text) return row.text[plan];
  if (row.access) return row.access.includes(plan) ? true : false;
  if (row.limit) {
    const l = planLimits(plan)[row.limit];
    if (typeof l === "boolean") return l;
    if (!l) return false;
    return row.monthly ? `${l}/mo` : String(l);
  }
  const v = n(quotas?.[plan], row.quota);
  if (v === null) return "—";
  if (v === "0") return false;
  return v === "Unlimited" ? "Unlimited" : `${v}/mo`;
}

// Which plan to recommend for the moment the paywall was opened.
export const PAYWALL_REASONS = {
  download: { title: "Download without the watermark", sub: "Paid plans include clean PDF + DOCX downloads every month.", recommend: "starter" },
  share: { title: "Share your resume link", sub: "Paid plans include public share links — Starter 1, Professional 5, Premium 20.", recommend: "starter" },
  portfolio: { title: "Publish your Digital Portfolio", sub: "Digital Portfolio publishing is included with Professional and Premium.", recommend: "professional" },
  resumes: { title: "You've reached your saved-resume limit", sub: "Keep more tailored versions: Starter 3, Professional 10, Premium 30.", recommend: "professional" },
  template: { title: "Unlock this template", sub: "Free includes 5 basic templates. Starter unlocks all 26; Professional adds Smart Layouts and AI Style Studio design.", recommend: "starter" },
  smartlayout: { title: "Unlock Smart Layouts", sub: "Smart Layouts and custom design in AI Style Studio are included with Professional and Premium.", recommend: "professional" },
  limit: { title: "You've used your free tries", sub: "Pick a plan to keep building, matching and downloading. Your work is saved — it'll be right here when you upgrade.", recommend: "starter" },
  anonLimit: { title: "Create a free account to keep going", sub: "You've used the free tries for visitors. A free account (no card) gives you more — or pick a plan.", recommend: "starter" },
  quota: { title: "You've reached this month's limit", sub: "Every plan has a monthly allowance for each AI tool. Upgrade for more.", recommend: "professional" },
  jobsearch: { title: "Unlock your full job search", sub: "Job Search, saved searches, application previews and the Tracker are in Premium.", recommend: "premium" },
  linkedin: { title: "Generate your LinkedIn profile", sub: "LinkedIn profile generation is part of Premium.", recommend: "premium" },
  anon: { title: "Sign up free to keep going", sub: "You've used the free tries for visitors. Create a free account (no card) for more, or pick a plan.", recommend: "starter" },
  default: { title: "Choose your plan", sub: "Upgrade anytime. Cancel anytime and keep access until the end of the period.", recommend: "professional" },
};

let catalogPromise = null;
// Live catalog from GET /api/ai/plans: { quotas, limits, free_template_idxs }.
// Also refreshes PLAN_LIMITS / FREE_TEMPLATE_IDXS so every helper above uses
// the server's numbers.
export function loadPlanCatalog(api) {
  if (!catalogPromise) {
    catalogPromise = fetch(`${api}/ai/plans`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (d.limits) PLAN_LIMITS = { ...PLAN_LIMITS, ...d.limits };
        if (Array.isArray(d.free_template_idxs)) FREE_TEMPLATE_IDXS = d.free_template_idxs;
        return d;
      })
      .catch(() => { catalogPromise = null; return {}; });
  }
  return catalogPromise;
}

export function loadPlanQuotas(api) {
  return loadPlanCatalog(api).then((d) => d.quotas || PLAN_QUOTAS);
}

// 402 "Upgrade to X" responses -> which paywall variant to show.
export function paywallReasonFor(url, detail = "") {
  const d = String(detail);
  if (/saved resume/i.test(d)) return "resumes";
  if (/Job Search|Talent Directory|Application Bundle/i.test(d)) return "jobsearch";
  if (/Smart Layouts/i.test(d)) return "smartlayout";
  if (/template/i.test(d)) return "template";
  if (/share link/i.test(d) || /\/share\b/.test(url)) return "share";
  if (/Portfolio/i.test(d) || /\/publish\b/.test(url)) return "portfolio";
  if (/download|export/i.test(d) || /\/export\//.test(url)) return "download";
  return "default";
}

// Open the plan picker from anywhere ("Upgrade" buttons, lock badges,
// locked templates). GlobalUpgradePrompt (mounted once in App.js) listens.
export const UPGRADE_EVENT = "rp360:upgrade-required";
export const openUpgrade = (reason = "default") => {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: { reason } }));
};


// Pre-flight before anything that creates a resume (e.g. AI generation), so a
// user at the saved-resume cap sees the plan picker BEFORE an AI credit is
// spent. Mirrors resume-service _enforce_resume_cap: bundle copies don't count.
// Fails open -- the server still enforces the cap on save.
export async function hasRoomForNewResume(api, plan) {
  const cap = planLimits(plan || "free").saved_resumes;
  if (cap === -1) return true;
  try {
    const r = await fetch(`${api}/my/resumes`, { credentials: "include" });
    if (!r.ok) return true;
    const list = await r.json();
    const owned = (Array.isArray(list) ? list : list?.resumes || []).filter((d) => !d.bundle_id).length;
    return owned < cap;
  } catch {
    return true;
  }
}
